/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("StartMenuWidget", ["WidgetGroupBase", "WidgetFactory"], function (context, cls) {
  /**
   * StartMenu widget.
   * @class StartMenuWidget
   * @memberOf classes
   * @extends classes.WidgetGroupBase
   */
  cls.StartMenuWidget = context.oo.Class(cls.WidgetGroupBase, function ($super) {
    return /** @lends classes.StartMenuWidget.prototype */ {
      __name: "StartMenuWidget",
      /**
       * Element that hold the text
       * @type {HTMLElement}
       */
      _textElement: null,
      /**
       * Element (button) that hold the title and expand/collapse children container.
       * Carries role="treeitem" + the disclosure aria-expanded / aria-controls — the button is
       * the focusable APG tree node; the surrounding <li role="none"> is a structural wrapper.
       * @type {HTMLElement}
       */
      _titleElement: null,
      /**
       * Right icon collapse/expanded
       * @type {HTMLElement}
       */
      _leftIcon: null,
      /**
       * @inheritDoc
       */
      constructor: function (opts) {
        $super.constructor.call(this, opts);
        this._textElement = this._element.getElementsByClassName(this.getBEMPartName("text"))[0];
        this._titleElement = this._element.getElementsByClassName(this.getBEMPartName("title"))[0];
        this._leftIcon = this._element.getElementsByClassName("left-icon")[0];

        // Open by default the StartMenuTitle
        this.toggleMenu();
      },

      /**
       * @inheritDoc
       */
      _initElement: function () {
        $super._initElement.call(this);
        this.setAcceptEventWhenWindowInactive(true);

        const titleElement = this._element.getElementsByClassName(this.getBEMPartName("title"))[0];
        const containerElement = this._element.getElementsByClassName(this.getBEMPartName("container"))[0];
        containerElement.id = this.getRootClassName() + "__container";
        // aria-owns pairs the treeitem with its sibling <ul role="group"> in the a11y tree
        // (the group is a DOM sibling of the button, not a descendant).
        cls.DOMElementHelper.updateAttribute(titleElement, "aria-controls", containerElement.id);
        cls.DOMElementHelper.updateAttribute(titleElement, "aria-owns", containerElement.id);
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        this._textElement = null;
        this._titleElement = null;
        this._leftIcon = null;
        $super.destroy.call(this);
      },

      /**
       * Toggles the menu state and updates the icon accordingly.
       */
      toggleMenu: function () {
        const open = this._element.classList.toggle("gbc_open");
        cls.DOMElementHelper.updateAttribute(this._titleElement, "aria-expanded", open);
        cls.DOMElementHelper.toggleClass(this._leftIcon, "zmdi-chevron-down", "zmdi-chevron-right", open);
      },

      /**
       * @inheritDoc
       */
      manageMouseClick: function (domEvent) {
        if (domEvent.target === null) {
          return true;
        } // just ignore qa click

        if (cls.DOMElementHelper.elementOrParent(domEvent.target, "gbc_StartMenuWidget__title") && this.isEnabled()) {
          // only click on group title are managed
          this.toggleMenu();
        }
        return false;
      },

      /**
       * Keyboard activation of the focused title (accessibility mode routes
       * keys to the DOM-focused widget before dialog accelerators). The title
       * is the only focusable this widget owns directly: focus on a child
       * command/group routes to that child widget instead.
       * @inheritDoc
       */
      managePriorityKeyDown: function (keyString, domKeyEvent, repeat) {
        if (this.isEnabled() && this._isActivationKey(keyString, repeat) && document.activeElement === this._titleElement) {
          this.toggleMenu();
          return true;
        }
        return $super.managePriorityKeyDown.call(this, keyString, domKeyEvent, repeat);
      },

      /**
       * Get the text element of the group
       * @returns {HTMLElement|null}
       */
      getTextElement: function () {
        return this._textElement;
      },

      /**
       * Set the text of the group
       * @param {string} text
       */
      setText: function (text) {
        // If no text is set. Don't update it to let the default translated title
        if (!text) {
          return;
        }
        cls.DOMElementHelper.updateAttribute(this._textElement, "title", text);
        this._textElement.textContent = text;
      },

      /**
       * Get the text of the group
       * @return {string}
       */
      getText: function () {
        return this._textElement.textContent;
      },

      setProcessing: function (isProcessing) {
        if (this.getElement()) {
          if (isProcessing) {
            this.getElement().setAttribute("processing", "processing");
          } else {
            this.getElement().removeAttribute("processing");
          }
        }
      }
    };
  });
  cls.WidgetFactory.registerBuilder("StartMenu", cls.StartMenuWidget);
});
